import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment, { type Moment } from 'moment';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { cloneDeep, first, has, isArray, isEmpty, isNil, isObject, noop } from 'lodash';
// Import Types
import type { DropdownOption } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectUserActivePartnerTimeZone } from 'store/slices/userData';
// Import UI Components
import { Modal, Button, Icon, BusyLoader, Tag } from '@geneui/components';
// Import Components
import { Scheduler } from 'components/common/Scheduler';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import RecipientSelectors from 'components/common/Scheduler/RecipientSelectors';
import RandomComponentWithHeadline from 'components/common/RandomComponentWithHeadline';
import ValidatableDropdown from 'components/common/ValidatableDropdown';
// Import Constants
import {
    ScheduleModalViews,
    l,
    MonthDayYearFormat,
    TimeFormatWithoutSeconds,
    ExportFileFormat,
    DateToISOStringFormat,
    ExportFileFormatLabels,
} from 'constants/common';
import { DefaultDatesDict } from './config';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { ReportHttpService, UtilsHttpService } from 'services/http';
import { Helpers } from 'services';
import { getCustomerJourneyFormFieldTranslatableErrorText } from 'services/customerJourney/helpers';
import { resultScheduleDataCreator } from 'services/schedular';
// Import Validation
import { ReportScheduleValidationSchema } from 'validators/schemas.validation';

const { mainView, dailyView, weeklyView, monthlyView, annuallyView, notRepeatView } = ScheduleModalViews;
const { cronStringDecoder, getDateWithTime, customMoment, customMomentWithoutTimezoneConversion, getPlusOneDayDate } =
    Helpers;

const ScheduleReportModal = ({ isVisibleModal, onCancel, onOk, reportId, reportType, isLoading }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const ActivePartnerTimeZone = useAppSelector(selectUserActivePartnerTimeZone) ?? 0;

    const formik = useFormik({
        initialValues: {
            startDate: customMoment().format(MonthDayYearFormat),
            endDate: customMoment(getPlusOneDayDate()).format(MonthDayYearFormat),
            startTime: customMoment().format(TimeFormatWithoutSeconds),
            datesDict: cloneDeep(DefaultDatesDict),
            selectedDateSelector: notRepeatView,
            modalView: notRepeatView,
            timeZone: ActivePartnerTimeZone,
            selectedFileFormats: [],
            selectedEmails: [],
            selectedFtps: [],
        },
        onSubmit: noop,
        validationSchema: ReportScheduleValidationSchema(),
    });

    const { values, setFieldValue, submitForm, errors, setValues } = formik;
    const {
        startDate,
        endDate,
        startTime,
        datesDict,
        selectedDateSelector,
        modalView,
        timeZone,
        selectedEmails,
        selectedFtps,
        selectedFileFormats,
    } = values;

    const [emails, setEmails] = useState<DropdownOption<number>[]>([]);
    const [ftpProviders, setFtpProviders] = useState<DropdownOption<number>[]>([]);
    const [isVisibleRecCount, setIsVisibleRecCount] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [selectedFileFormatsValues, setSelectedFileFormatsValues] = useState([]);

    useEffect(() => {
        setSelectedFileFormatsValues(
            selectedFileFormats?.map((value) => ({
                label: ExportFileFormatLabels[value],
                value: value,
            })),
        );
    }, [selectedFileFormats]);

    useEffect(() => {
        submitForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteChosenFormat = (e) => {
        const filteredSelectedValues = selectedFileFormats.filter((value) => +value !== +e);
        setFieldValueBuilder('selectedFileFormats', filteredSelectedValues);
    };

    const init = () => {
        setIsVisibleRecCount(true);
    };

    const startDateChangeHandler = (value) => {
        const fieldsValues = { ...values, startDate: value };
        if (selectedDateSelector === notRepeatView) {
            fieldsValues.endDate = value;
        }
        setValues(fieldsValues, true);
    };

    const setFieldValueBuilder = (filedKey, value) => {
        setFieldValue(filedKey, value, true);
    };

    const startTimeChangeHandler = (e) => {
        const { value } = e.target;
        setFieldValueBuilder('startTime', value);
    };

    const endDateChangeHandler = (value) => {
        setFieldValueBuilder('endDate', value);
    };

    const saveHandler = () => {
        const Schedule = resultScheduleDataCreator(
            startTime,
            startDate,
            endDate,
            selectedDateSelector,
            datesDict,
            timeZone,
        );

        const userIds = selectedEmails.map((item) => item.value);

        const ftpIds = selectedFtps.map((item) => item.value);

        const Providers = [
            { Provider: 0, Recepient: userIds, ExportTypes: selectedFileFormats },
            { Provider: 5, Recepient: ftpIds, ExportTypes: selectedFileFormats },
        ];

        onOk({ Providers: Providers, Schedule: Schedule, ReportId: reportId, Type: reportType });
    };

    const reset = () => {
        const dict = {
            dailyView: datesDict[dailyView],
            weeklyView: datesDict[weeklyView],
            monthlyView: datesDict[monthlyView],
            annuallyView: datesDict[annuallyView],
        };

        dict[modalView] = {
            days: [],
            weeks: [],
            months: [],
            years: [],
            day: '',
            month: '',
            year: '',
            isActivePicker: false,
        };
        setFieldValueBuilder('datesDict', datesDict);
        setFieldValueBuilder('startDate', customMoment().format(MonthDayYearFormat));
        setFieldValueBuilder('endDate', customMoment().format(MonthDayYearFormat));
        setFieldValueBuilder('startTime', customMoment().format(TimeFormatWithoutSeconds));
        setFieldValueBuilder('timeZone', ActivePartnerTimeZone);
        setFieldValueBuilder('selectedDateSelector', notRepeatView);
        setFieldValueBuilder('modalView', mainView);
        setFieldValueBuilder('selectedFtps', []);
        setFieldValueBuilder('selectedEmails', []);
        setFieldValueBuilder('selectedFileFormats', []);
    };

    const setDictData = (data, viewType) => {
        const obj = { ...datesDict };
        obj[viewType] = data;
        setFieldValue('datesDict', obj, true);
    };

    const { getPartnerProviders, getReportSchedule } = useCancelableRequests({
        getReportSchedule: ReportHttpService.getReportSchedule,
        getPartnerProviders: UtilsHttpService.getPartnerProviders,
    });

    const getProviders = () => {
        doGetRequest(getPartnerProviders.request, {
            successCallback: (Data) => {
                const emailList: DropdownOption<number>[] = [];
                const ftpProvidersList: DropdownOption<number>[] = [];

                Data.forEach(({ Type, Name, Id }) => {
                    Type === 0 && emailList.push({ label: Name, value: Id });
                    (Type === 5 || Type === 6) && ftpProvidersList.push({ label: Name, value: Id });
                });

                setEmails(emailList);
                setFtpProviders(ftpProvidersList);
                getSchedule(emailList, ftpProvidersList);
            },
        });
    };

    const getSchedule = (emails, ftps) => {
        doGetRequest(getReportSchedule.request, {
            queryString: { reportId: reportId, type: reportType },
            successCallback: (Data) => {
                let scheduleData = cronStringDecoder();

                let startDate: Moment | string = customMoment();
                let endDate: Moment | string = customMoment();
                let startTime: string = customMoment().format(TimeFormatWithoutSeconds);
                const { Schedule, Providers } = Data;
                if (isArray(Providers)) {
                    const emailRecipients = first(Providers);
                    setFieldValueBuilder('selectedFileFormats', first(Providers)?.ExportTypes);

                    if (
                        isObject(emailRecipients) &&
                        has(emailRecipients, 'Recepient') &&
                        isArray(emailRecipients.Recepient)
                    ) {
                        const selectedEmailsResult = [];

                        if (!isEmpty(emails)) {
                            emailRecipients.Recepient.forEach((recepientId, index) => {
                                selectedEmailsResult[index] = first(
                                    emails.filter((email) => {
                                        return email.value === recepientId;
                                    }),
                                );
                            });
                        }
                        setFieldValueBuilder('selectedEmails', selectedEmailsResult);
                        setFieldValueBuilder('selectedFileFormats', emailRecipients.ExportTypes);
                    }
                    let ftpRecepients = Providers[1].Recepient;
                    if (!isNil(Providers[2])) {
                        ftpRecepients = ftpRecepients.concat(Providers[2].Recepient);
                    }

                    if (isObject(ftpRecepients)) {
                        const selectedFtpssResult = [];
                        if (!isEmpty(ftps)) {
                            ftpRecepients.forEach((recepientId, index) => {
                                selectedFtpssResult[index] = first(
                                    ftps.filter((ftp) => {
                                        return ftp.value === recepientId;
                                    }),
                                );
                            });
                        }
                        setFieldValueBuilder('selectedFtps', selectedFtpssResult);
                    }
                }

                if (!isNil(Schedule)) {
                    const { StartDate, EndDate, CronTime, CronSchedule, TimeZone } = Schedule;
                    setFieldValueBuilder('timeZone', TimeZone);
                    if (isEmpty(CronTime)) {
                        startTime = customMomentWithoutTimezoneConversion(StartDate).format(TimeFormatWithoutSeconds);
                    } else {
                        setIsVisibleRecCount(true);
                        scheduleData = cronStringDecoder(CronSchedule);
                        startTime = getDateWithTime(customMoment(), CronTime.split(':')).format(
                            TimeFormatWithoutSeconds,
                        );
                        setFieldValueBuilder('selectedDateSelector', scheduleData.viewType);
                    }
                    startDate = moment(StartDate, DateToISOStringFormat).format(MonthDayYearFormat);
                    endDate = moment(EndDate, DateToISOStringFormat).format(MonthDayYearFormat);
                }
                setDictData(scheduleData, scheduleData.viewType);
                setFieldValue('startDate', startDate, true);
                setFieldValue('endDate', endDate, true);
                setFieldValue('startTime', startTime, true);
            },
        }).then(() => setIsLoaded(true));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getProviders, [isVisibleModal]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    //use ModalFooter component from labels modal
    const getFooter = () => {
        return (
            <>
                <Button appearance="minimal" onClick={onCancel}>
                    {t(l.Close)}
                </Button>
                <Button appearance="minimal" onClick={reset}>
                    {t(l.Reset)}
                </Button>
                <ButtonWithLoader color="primary" onClick={saveHandler} isLoading={isLoading}>
                    {t(l.Save)}
                </ButtonWithLoader>
            </>
        );
    };

    return (
        <Modal
            background="dark-background"
            title={<div className="shedule-title">{t(l.ScheduleReport)}</div>}
            position="center"
            size="content-size"
            closable={false}
            onCancel={onCancel}
            closeOnClickOutside={true}
            visible={isVisibleModal}
            footer={getFooter()}
            className="scheduler-modal-wrap"
        >
            <BusyLoader isBusy={!isLoaded} type="spinner" spinnerSize="big">
                {isLoaded && (
                    <>
                        <div className="providers-wrap">
                            <div className="providers-wrap-inner">
                                <div className="title-wrapper">
                                    <p className="title-wrapper-text">{t(l.ExportFileFormat)}</p>
                                    <span className="title-wrapper-line" />
                                </div>
                                <div className="providers-wrap-row">
                                    <ValidatableDropdown
                                        data={ExportFileFormat}
                                        value={selectedFileFormats}
                                        isMultiSelect={true}
                                        onChange={(values) =>
                                            setFieldValueBuilder(
                                                'selectedFileFormats',
                                                values.map(({ value }) => value),
                                            )
                                        }
                                        errorText={getCustomerJourneyFormFieldTranslatableErrorText(
                                            t,
                                            errors?.selectedFileFormats,
                                        )}
                                        isValid={!errors.selectedFileFormats}
                                    />
                                    <RandomComponentWithHeadline className={'crm-form-single-input-equal'}>
                                        <div className={'crm-multi-select-country-section-body'}>
                                            <div className="crm-multi-select-country-inner">
                                                <p>{t(l.SelectedItems)}</p>
                                                {selectedFileFormatsValues &&
                                                    selectedFileFormatsValues.map(({ label, value }) => (
                                                        <Tag
                                                            name={label}
                                                            key={value}
                                                            appearance="minimal"
                                                            cornerRadius="smooth-radius"
                                                            className={'crm-multi-select-country-section__tag'}
                                                            icons={
                                                                <Icon
                                                                    type={'bc-icon-clear'}
                                                                    onClick={() => deleteChosenFormat(value)}
                                                                />
                                                            }
                                                        />
                                                    ))}
                                            </div>
                                        </div>
                                    </RandomComponentWithHeadline>
                                </div>
                            </div>
                        </div>

                        <RecipientSelectors
                            emailRecipientsList={emails}
                            ftpRecipientsList={ftpProviders}
                            setSelectedEmailRecipients={(value) => setFieldValueBuilder('selectedEmails', value)}
                            setSelectedFtpRecipients={(value) => setFieldValueBuilder('selectedFtps', value)}
                            selectedEmailRecipients={selectedEmails}
                            selectedFtpRecipients={selectedFtps}
                        />

                        <Scheduler
                            dictData={datesDict}
                            view={modalView}
                            startDate={startDate}
                            endDate={endDate}
                            startTime={startTime}
                            setView={(value) => setFieldValueBuilder('modalView', value)}
                            setDictData={setDictData}
                            selectedDateSelector={selectedDateSelector}
                            setStartTime={startTimeChangeHandler}
                            setEndDate={endDateChangeHandler}
                            setStartDate={startDateChangeHandler}
                            startDateErrorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.startDate)}
                            endDateErrorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.endDate)}
                            getFooter={getFooter}
                            isVisibleRecCount={isVisibleRecCount}
                            timeZone={timeZone}
                            setTimeZone={(value) => setFieldValueBuilder('timeZone', value)}
                            viewMode="fullView"
                            setSelectedDateSelector={(value) => setFieldValueBuilder('selectedDateSelector', value)}
                        />
                    </>
                )}
            </BusyLoader>
        </Modal>
    );
};

ScheduleReportModal.propTypes = {
    isVisibleModal: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    reportType: PropTypes.number.isRequired,
    reportId: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onOk: PropTypes.func.isRequired,
};

export default memo(ScheduleReportModal);
